// ─────────────────────────────────────────────────────────────────────────────
// CustomEvalModal
//
// Create or edit a custom eval preset. All fields mirror prop_firm_presets
// so the simulator can use the result without modification.
// ─────────────────────────────────────────────────────────────────────────────

import { useState } from "react";
import { C } from "../../lib/colors";
import { Btn } from "../../components/ui/Btn";

const DRAWDOWN_TYPES = [
  { value: "trailing_eod",       label: "Trailing EOD — high-water mark resets end of day" },
  { value: "trailing_intraday",  label: "Trailing Intraday — high-water mark updates live" },
  { value: "static",             label: "Static — fixed floor from starting balance" },
];

const EMPTY_FORM = {
  firm_name:           "",
  plan_name:           "",
  account_size:        50000,
  starting_balance:    50000,
  profit_target:       3000,
  max_drawdown:        2000,
  drawdown_type:       "trailing_eod",
  max_daily_loss:      "",       // empty = no DLL
  daily_loss_type:     "hard",
  min_trading_days:    0,
  consistency_enabled: false,
  consistency_max_pct: 30,       // stored as percent in form, converted to decimal on save
  notes:               "",
  source_url:          "",
};

export function CustomEvalModal({ isOpen, onClose, onSave, editPreset }) {
  const [form, setForm]     = useState(editPreset ? presetToForm(editPreset) : EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState(null);

  if (!isOpen) return null;

  const set = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  const handleSave = async () => {
    // Validate required fields
    if (!form.firm_name.trim()) { setError("Firm name is required."); return; }
    if (!form.max_drawdown || Number(form.max_drawdown) <= 0) {
      setError("Max drawdown must be greater than 0."); return;
    }
    setSaving(true);
    setError(null);
    try {
      await onSave(formToPreset(form));
      onClose();
    } catch (e) {
      setError(e.message || "Failed to save.");
      setSaving(false);
    }
  };

  return (
    <div
      style={{
        position: "fixed", inset: 0,
        background: "rgba(0,0,0,0.75)",
        display: "flex", alignItems: "center", justifyContent: "center",
        zIndex: 1000, padding: 16,
      }}
      onClick={onClose}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: "#0f1322",
          border: `1px solid ${C.border}`,
          borderRadius: 12,
          padding: 24,
          maxWidth: 560,
          width: "100%",
          maxHeight: "90vh",
          overflowY: "auto",
        }}
      >
        <h2 style={{ fontSize: 18, fontWeight: 800, color: C.text, margin: "0 0 4px" }}>
          {editPreset ? "Edit Eval Preset" : "Add Custom Eval"}
        </h2>
        <p style={{ fontSize: 12, color: C.muted, margin: "0 0 20px", lineHeight: 1.5 }}>
          Define the rules for your eval. The simulator will replay your trade history against these rules.
        </p>

        {/* ── Identity ───────────────────────────────────────────── */}
        <FieldGroup label="Firm & Plan">
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <Field label="Firm name *" hint="e.g. TopStep, Apex, My Firm">
              <Input value={form.firm_name} onChange={v => set("firm_name", v)} placeholder="Firm name" />
            </Field>
            <Field label="Plan name" hint="e.g. $50K Combine, Eval 1">
              <Input value={form.plan_name} onChange={v => set("plan_name", v)} placeholder="Plan name" />
            </Field>
          </div>
        </FieldGroup>

        {/* ── Account size ───────────────────────────────────────── */}
        <FieldGroup label="Account">
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <Field label="Account size ($)" hint="Starting balance for the sim">
              <NumberInput
                value={form.starting_balance}
                onChange={v => { set("starting_balance", v); set("account_size", v); }}
                min={1000} step={1000}
              />
            </Field>
            <Field label="Profit target ($)" hint="Leave blank for funded (no target)">
              <NumberInput
                value={form.profit_target}
                onChange={v => set("profit_target", v)}
                min={0} step={100}
                allowEmpty
              />
            </Field>
          </div>
        </FieldGroup>

        {/* ── Drawdown ────────────────────────────────────────────── */}
        <FieldGroup label="Drawdown">
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
            <Field label="Max drawdown ($) *">
              <NumberInput
                value={form.max_drawdown}
                onChange={v => set("max_drawdown", v)}
                min={100} step={100}
              />
            </Field>
            <Field label="Drawdown type">
              <Select
                value={form.drawdown_type}
                onChange={v => set("drawdown_type", v)}
                options={DRAWDOWN_TYPES}
              />
            </Field>
          </div>
        </FieldGroup>

        {/* ── Daily loss limit ────────────────────────────────────── */}
        <FieldGroup label="Daily Loss Limit">
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <Field label="Max daily loss ($)" hint="Leave blank for no DLL">
              <NumberInput
                value={form.max_daily_loss}
                onChange={v => set("max_daily_loss", v)}
                min={0} step={50}
                allowEmpty
              />
            </Field>
            <Field label="DLL type" hint="Hard = account blown. Soft = day halted.">
              <Select
                value={form.daily_loss_type}
                onChange={v => set("daily_loss_type", v)}
                options={[
                  { value: "hard", label: "Hard — account blown" },
                  { value: "soft", label: "Soft — trading halted for the day" },
                ]}
                disabled={!form.max_daily_loss}
              />
            </Field>
          </div>
        </FieldGroup>

        {/* ── Min trading days ───────────────────────────────────── */}
        <FieldGroup label="Minimum Trading Days">
          <Field label="Min days to pass" hint="0 = no minimum">
            <NumberInput
              value={form.min_trading_days}
              onChange={v => set("min_trading_days", v)}
              min={0} step={1}
            />
          </Field>
        </FieldGroup>

        {/* ── Consistency rule ────────────────────────────────────── */}
        <FieldGroup label="Consistency Rule">
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
            <Toggle
              value={form.consistency_enabled}
              onChange={v => set("consistency_enabled", v)}
              label="Enable consistency rule"
            />
          </div>
          {form.consistency_enabled && (
            <Field
              label="Max single day as % of total profit"
              hint="e.g. 30 means no single day can be more than 30% of total profit"
            >
              <NumberInput
                value={form.consistency_max_pct}
                onChange={v => set("consistency_max_pct", v)}
                min={1} max={100} step={5}
                suffix="%"
              />
            </Field>
          )}
        </FieldGroup>

        {/* ── Notes / URL ─────────────────────────────────────────── */}
        <FieldGroup label="Notes (optional)">
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <Field label="Notes" hint="Any caveats or reminders">
              <Input value={form.notes} onChange={v => set("notes", v)} placeholder="e.g. No overnight holds" />
            </Field>
            <Field label="Official rules URL" hint="Link to the firm's ruleset page">
              <Input value={form.source_url} onChange={v => set("source_url", v)} placeholder="https://..." />
            </Field>
          </div>
        </FieldGroup>

        {error && (
          <div style={{
            padding: "8px 12px", background: C.red + "15",
            border: `1px solid ${C.red}40`, borderRadius: 6,
            fontSize: 12, color: C.red, marginBottom: 14,
          }}>
            ⚠ {error}
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 4 }}>
          <Btn variant="ghost" onClick={onClose} disabled={saving}>Cancel</Btn>
          <Btn onClick={handleSave} disabled={saving}>
            {saving ? "Saving…" : editPreset ? "Save Changes" : "Add Eval"}
          </Btn>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Form ↔ Preset conversion
// ─────────────────────────────────────────────────────────────────────────────

function presetToForm(p) {
  return {
    firm_name:           p.firm_name || "",
    plan_name:           p.plan_name || "",
    account_size:        Number(p.account_size || p.starting_balance || 50000),
    starting_balance:    Number(p.starting_balance || 50000),
    profit_target:       p.profit_target != null ? Number(p.profit_target) : "",
    max_drawdown:        Number(p.max_drawdown || 0),
    drawdown_type:       p.drawdown_type || "trailing_eod",
    max_daily_loss:      p.max_daily_loss != null ? Number(p.max_daily_loss) : "",
    daily_loss_type:     p.daily_loss_type || "hard",
    min_trading_days:    Number(p.min_trading_days || 0),
    consistency_enabled: Boolean(p.consistency_enabled),
    consistency_max_pct: p.consistency_max_pct != null ? Number(p.consistency_max_pct) * 100 : 30,
    notes:               p.notes || "",
    source_url:          p.source_url || "",
  };
}

function formToPreset(form) {
  return {
    firm_name:           form.firm_name.trim(),
    plan_name:           form.plan_name.trim(),
    account_size:        Number(form.starting_balance),
    starting_balance:    Number(form.starting_balance),
    profit_target:       form.profit_target !== "" ? Number(form.profit_target) : null,
    max_drawdown:        Number(form.max_drawdown),
    drawdown_type:       form.drawdown_type,
    max_daily_loss:      form.max_daily_loss !== "" ? Number(form.max_daily_loss) : null,
    daily_loss_type:     form.daily_loss_type,
    min_trading_days:    Number(form.min_trading_days) || 0,
    consistency_enabled: Boolean(form.consistency_enabled),
    consistency_max_pct: form.consistency_enabled ? Number(form.consistency_max_pct) / 100 : 0.30,
    notes:               form.notes.trim() || null,
    source_url:          form.source_url.trim() || null,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Form primitives
// ─────────────────────────────────────────────────────────────────────────────

function FieldGroup({ label, children }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 600, marginBottom: 8 }}>
        {label}
      </div>
      {children}
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <div style={{ fontSize: 11, color: C.sub, marginBottom: 4 }}>
        {label}
        {hint && <span style={{ color: C.muted, fontSize: 10, marginLeft: 6 }}>{hint}</span>}
      </div>
      {children}
    </div>
  );
}

function Input({ value, onChange, placeholder }) {
  return (
    <input
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      style={{
        width: "100%", padding: "8px 10px",
        background: "#1a1d2e", border: `1px solid ${C.border}`,
        borderRadius: 6, color: C.text, fontSize: 13, outline: "none",
        boxSizing: "border-box",
      }}
    />
  );
}

function NumberInput({ value, onChange, min, max, step, allowEmpty, suffix }) {
  return (
    <div style={{ position: "relative" }}>
      <input
        type="number"
        value={value}
        min={min} max={max} step={step}
        onChange={e => {
          const v = e.target.value;
          if (allowEmpty && v === "") { onChange(""); return; }
          const n = Number(v);
          if (!isNaN(n)) onChange(n);
        }}
        style={{
          width: "100%", padding: suffix ? "8px 28px 8px 10px" : "8px 10px",
          background: "#1a1d2e", border: `1px solid ${C.border}`,
          borderRadius: 6, color: C.text, fontSize: 13, outline: "none",
          boxSizing: "border-box",
        }}
      />
      {suffix && (
        <span style={{
          position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)",
          fontSize: 12, color: C.muted, pointerEvents: "none",
        }}>
          {suffix}
        </span>
      )}
    </div>
  );
}

function Select({ value, onChange, options, disabled }) {
  return (
    <select
      value={value}
      onChange={e => onChange(e.target.value)}
      disabled={disabled}
      style={{
        width: "100%", padding: "8px 10px",
        background: "#1a1d2e", border: `1px solid ${C.border}`,
        borderRadius: 6, color: disabled ? C.muted : C.text,
        fontSize: 13, outline: "none", cursor: disabled ? "not-allowed" : "pointer",
        boxSizing: "border-box",
      }}
    >
      {options.map(o => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  );
}

function Toggle({ value, onChange, label }) {
  return (
    <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
      <div
        onClick={() => onChange(!value)}
        style={{
          width: 36, height: 20, borderRadius: 10,
          background: value ? C.blue : C.border,
          position: "relative", transition: "background 0.2s", flexShrink: 0,
        }}
      >
        <div style={{
          position: "absolute", top: 3,
          left: value ? 19 : 3,
          width: 14, height: 14, borderRadius: "50%",
          background: "#fff", transition: "left 0.2s",
        }} />
      </div>
      <span style={{ fontSize: 12, color: C.sub }}>{label}</span>
    </label>
  );
}
